import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getPlatformRevenueOverview } from "@/services/firebase/revenue";
import FinancialDateRangePicker from "@/components/financial/FinancialDateRangePicker";
import { useFinancialDateRange, periodFromRange } from "@/store/financialDateRange";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import {
  DollarSign,
  Coins,
  Users,
  TrendingUp,
  TrendingDown,
  Smartphone,
  Wallet,
  Gift,
  ShoppingCart,
  Download,
} from "lucide-react";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { AdminDataTable } from "@/components/admin-ui/admin-data-table";
import type { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { AdminLoadingSkeleton } from "@/components/admin-ui/admin-loading-skeleton";
import { AdminErrorAlert } from "@/components/admin-ui/admin-error-alert";
import { AdminEmptyState } from "@/components/admin-ui/admin-empty-state";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type {
  PeriodCoreMetrics,
  PlatformDailyEntry,
  TopDrama,
  CompanyRow,
  ProductSale,
} from "@/types/revenue";

// ─── Reward 類型中文名稱 ───
const GRANT_TYPE_LABELS: Record<string, string> = {
  daily_checkin: "每日簽到",
  mission_reward: "任務獎勵",
  app_usage_reward: "App 使用獎勵",
  ad_reward: "看廣告獎勵",
  admin_grant: "後台發放",
  gift_code: "兌換碼",
};

function grantLabel(type: string): string {
  return GRANT_TYPE_LABELS[type] ?? type;
}

// ─── 格式化 ───
const fmtNTD = (n: number | undefined): string =>
  `NTD ${(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const fmtCoin = (n: number | undefined): string =>
  (n ?? 0).toLocaleString();

const fmtPct = (n: number | undefined, digits = 1): string =>
  `${((n ?? 0) * 100).toFixed(digits)}%`;

function delta(current: number, prev: number): { abs: number; pct: number; dir: "up" | "down" | "flat" } {
  if (prev === 0) {
    if (current === 0) return { abs: 0, pct: 0, dir: "flat" };
    return { abs: current, pct: current > 0 ? 1 : -1, dir: current > 0 ? "up" : "down" };
  }
  const abs = current - prev;
  const pct = abs / Math.abs(prev);
  return { abs, pct, dir: abs > 0 ? "up" : abs < 0 ? "down" : "flat" };
}

function DeltaBadge({
  d,
  inverse = false,
}: {
  d: { pct: number; dir: "up" | "down" | "flat" };
  inverse?: boolean;
}) {
  if (d.dir === "flat") {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const good = inverse ? d.dir === "down" : d.dir === "up";
  const Icon = d.dir === "up" ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        good ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
      }`}
    >
      <Icon className="h-3 w-3" />
      {fmtPct(Math.abs(d.pct), 1)}
    </span>
  );
}

// ─── KPI Card ───
function KpiCard({
  icon,
  label,
  value,
  sub,
  delta,
  deltaInverse = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: React.ReactNode;
  delta?: { pct: number; dir: "up" | "down" | "flat" };
  deltaInverse?: boolean;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {icon}
            {label}
          </div>
          {delta && <DeltaBadge d={delta} inverse={deltaInverse} />}
        </div>
        <div className="text-xl font-bold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
}

// ─── Tables ───
const topDramaColumns: AdminTableColumn[] = [
  { key: "drama_title", label: "劇集", sortable: true },
  { key: "company_name", label: "片商", sortable: true, hideBelow: "md" },
  { key: "unlock_count", label: "解鎖數", sortable: true, hideBelow: "md" },
  { key: "paid_coin_used", label: "Paid coin", sortable: true },
  { key: "free_coin_used", label: "Free coin", sortable: true, hideBelow: "lg" },
  { key: "unique_users", label: "用戶數", sortable: true, hideBelow: "lg" },
  { key: "paid_coin_rate", label: "費率", sortable: true, hideBelow: "lg" },
  { key: "payout_ntd", label: "分潤", sortable: true },
];

const companyColumns: AdminTableColumn[] = [
  { key: "company_name", label: "片商", sortable: true },
  { key: "paid_coin_used", label: "Paid coin", sortable: true },
  { key: "free_coin_used", label: "Free coin", sortable: true, hideBelow: "md" },
  { key: "payout_ntd", label: "分潤", sortable: true },
  { key: "transaction_count", label: "交易數", sortable: true, hideBelow: "lg" },
  { key: "unique_users", label: "用戶數", sortable: true, hideBelow: "lg" },
];

const productColumns: AdminTableColumn[] = [
  { key: "product_id", label: "金幣包", sortable: true },
  { key: "transaction_count", label: "售出包數", sortable: true },
  { key: "coins", label: "金幣總量", sortable: true },
  { key: "ntd", label: "營收 NTD", sortable: true },
  { key: "share", label: "佔比", sortable: true },
];

// ─── CSV export helper ───
function downloadCsv(filename: string, rows: string[][]): void {
  const content = "\uFEFF" + rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvEscape(v: any): string {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes("\"") || s.includes("\n")) {
    return `"${s.replace(/"/g, "\"\"")}"`;
  }
  return s;
}

// ─── Main ───
export default function RevenueDashboard() {
  const navigate = useNavigate();
  const { start } = useFinancialDateRange();
  const period = periodFromRange(start);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["platform-revenue-overview", period],
    queryFn: () => getPlatformRevenueOverview({ period, compare: true }),
  });

  const summary = data?.summary;
  const prev = data?.prev;
  const wallet = data?.wallet_snapshot;
  const daily: PlatformDailyEntry[] = data?.daily ?? [];
  const topDramas: TopDrama[] = data?.top_dramas ?? [];
  const byCompany: CompanyRow[] = data?.by_company ?? [];
  const productSales: ProductSale[] = data?.product_sales ?? [];

  const deltas = useMemo(() => {
    if (!summary || !prev) return null;
    return {
      iap_gross: delta(summary.iap.gross_ntd, prev.iap.gross_ntd),
      iap_net: delta(summary.iap.net_ntd, prev.iap.net_ntd),
      payout: delta(summary.payout_to_studios_ntd, prev.payout_to_studios_ntd),
      margin: delta(summary.platform_margin_ntd, prev.platform_margin_ntd),
      paying_users: delta(summary.paying_users, prev.paying_users),
      active_users: delta(summary.active_users, prev.active_users),
      paid_sold: delta(summary.paid_coin_flow.sold, prev.paid_coin_flow.sold),
      paid_consumed: delta(summary.paid_coin_flow.consumed, prev.paid_coin_flow.consumed),
      free_granted: delta(summary.free_coin_flow.granted, prev.free_coin_flow.granted),
      free_consumed: delta(summary.free_coin_flow.consumed, prev.free_coin_flow.consumed),
    };
  }, [summary, prev]);

  const handleExport = () => {
    if (!summary || !wallet) return;
    const rows: string[][] = [
      ["區塊", "項目", "數值"],
      ["摘要", "期間", data!.period],
      ["摘要", "IAP 總收入 (NTD)", String(summary.iap.gross_ntd)],
      ["摘要", "iOS gross", String(summary.iap.by_platform.ios.gross_ntd)],
      ["摘要", "Android gross", String(summary.iap.by_platform.android.gross_ntd)],
      ["摘要", "Apple 抽成 (%)", (summary.iap.by_platform.ios.fee_rate * 100).toFixed(1)],
      ["摘要", "Google 抽成 (%)", (summary.iap.by_platform.android.fee_rate * 100).toFixed(1)],
      ["摘要", "平台抽成合計 (NTD)", String(summary.iap.fee_ntd)],
      ["摘要", "平台淨收入 (NTD)", String(summary.iap.net_ntd)],
      ["摘要", "付給片商 (NTD)", String(summary.payout_to_studios_ntd)],
      ["摘要", "平台毛利 (NTD)", String(summary.platform_margin_ntd)],
      ["摘要", "毛利率 (%)", (summary.margin_ratio * 100).toFixed(2)],
      ["摘要", "付費用戶數", String(summary.paying_users)],
      ["摘要", "活躍用戶數", String(summary.active_users)],
      ["摘要", "ARPPU (NTD)", String(summary.arppu_ntd)],
      ["Paid Coin", "本期售出", String(summary.paid_coin_flow.sold)],
      ["Paid Coin", "本期消費", String(summary.paid_coin_flow.consumed)],
      ["Paid Coin", "消化率 (%)", (summary.paid_coin_flow.consumption_ratio * 100).toFixed(2)],
      ["Paid Coin", "平均實現價值 (NTD/coin)", String(summary.paid_coin_flow.avg_realized_value)],
      ["Paid Coin", "錢包總餘額 (當下)", String(wallet.paid_coin_total)],
      ["Free Coin", "本期發放", String(summary.free_coin_flow.granted)],
      ["Free Coin", "本期消費", String(summary.free_coin_flow.consumed)],
      ["Free Coin", "錢包總餘額 (當下)", String(wallet.free_coin_total)],
    ];
    for (const [type, b] of Object.entries(summary.free_coin_flow.granted_by_type)) {
      rows.push(["Free Coin 發放 by type", grantLabel(type), String(b.coins)]);
    }
    downloadCsv(`platform_revenue_${data!.period}.csv`, rows);
  };

  return (
    <AdminPage>
      <AdminPageHeader
        title="營收總覽"
        subtitle="Reel+ 平台財務、金幣經濟、內容表現儀表板"
        loading={isLoading}
        onRefresh={() => refetch()}
        actions={
          <>
            <FinancialDateRangePicker mode="month" />
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={!summary}
            >
              <Download className="h-4 w-4 mr-1" />
              匯出摘要
            </Button>
          </>
        }
      />

      {isLoading && <AdminLoadingSkeleton variant="mixed" />}
      {error && <AdminErrorAlert message={`載入失敗: ${(error as Error).message}`} />}

      {!isLoading && summary && data && (
        <div className="space-y-6">
          {/* === Section 1 — 平台財務摘要 === */}
          <section>
            <h2 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              平台財務摘要
              {data.prev_period && (
                <Badge variant="outline" className="text-[10px] font-normal">
                  比較 {data.prev_period}
                </Badge>
              )}
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              <KpiCard
                icon={<ShoppingCart className="h-3.5 w-3.5" />}
                label="IAP 總收入"
                value={fmtNTD(summary.iap.gross_ntd)}
                sub={
                  <>
                    iOS {fmtNTD(summary.iap.by_platform.ios.gross_ntd)} / Android{" "}
                    {fmtNTD(summary.iap.by_platform.android.gross_ntd)}
                  </>
                }
                delta={deltas?.iap_gross}
              />
              <KpiCard
                icon={<Smartphone className="h-3.5 w-3.5" />}
                label="平台抽成"
                value={`-${fmtNTD(summary.iap.fee_ntd)}`}
                sub={
                  <>
                    Apple{" "}
                    {summary.iap.by_platform.ios.fee_rate === 0 &&
                    summary.iap.by_platform.ios.gross_ntd > 0 ? (
                      <span className="text-amber-600 dark:text-amber-400">未設定</span>
                    ) : (
                      fmtPct(summary.iap.by_platform.ios.fee_rate)
                    )}{" "}
                    · Google{" "}
                    {summary.iap.by_platform.android.fee_rate === 0 &&
                    summary.iap.by_platform.android.gross_ntd > 0 ? (
                      <span className="text-amber-600 dark:text-amber-400">未設定</span>
                    ) : (
                      fmtPct(summary.iap.by_platform.android.fee_rate)
                    )}
                  </>
                }
              />
              <KpiCard
                icon={<DollarSign className="h-3.5 w-3.5" />}
                label="平台淨收入"
                value={fmtNTD(summary.iap.net_ntd)}
                sub="扣 Apple/Google 抽成"
                delta={deltas?.iap_net}
              />
              <KpiCard
                icon={<DollarSign className="h-3.5 w-3.5" />}
                label="付給片商"
                value={fmtNTD(summary.payout_to_studios_ntd)}
                sub="本期消費 × 各劇分潤費率"
                delta={deltas?.payout}
                deltaInverse
              />
              <KpiCard
                icon={<DollarSign className="h-3.5 w-3.5" />}
                label="平台毛利"
                value={fmtNTD(summary.platform_margin_ntd)}
                sub={`毛利率 ${fmtPct(summary.margin_ratio, 1)}`}
                delta={deltas?.margin}
              />
              <KpiCard
                icon={<Users className="h-3.5 w-3.5" />}
                label="付費用戶"
                value={summary.paying_users.toLocaleString()}
                sub={`ARPPU ${fmtNTD(summary.arppu_ntd)} · 活躍 ${summary.active_users.toLocaleString()}`}
                delta={deltas?.paying_users}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              註：IAP 收入 vs 片商分潤屬於不同時序事件（這月賣的 coin 不一定這月花）。毛利為經營口徑概估，非期間結清。
            </p>
          </section>

          {/* === Section 2 — 金幣經濟 === */}
          <section>
            <h2 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Coins className="h-4 w-4" />
              金幣經濟
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Paid coin */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Wallet className="h-4 w-4" />
                    Paid Coin 流動
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">本期售出</div>
                      <div className="font-medium tabular-nums flex items-center gap-2">
                        {fmtCoin(summary.paid_coin_flow.sold)} coin
                        {deltas && <DeltaBadge d={deltas.paid_sold} />}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">本期消費</div>
                      <div className="font-medium tabular-nums flex items-center gap-2">
                        {fmtCoin(summary.paid_coin_flow.consumed)} coin
                        {deltas && <DeltaBadge d={deltas.paid_consumed} />}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">消化率</div>
                      <div className="font-medium tabular-nums">
                        {fmtPct(summary.paid_coin_flow.consumption_ratio, 1)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">平均實現價值</div>
                      <div className="font-medium tabular-nums">
                        {summary.paid_coin_flow.avg_realized_value.toFixed(4)} NTD/coin
                      </div>
                    </div>
                    <div className="col-span-2 pt-2 border-t">
                      <div className="text-xs text-muted-foreground">目前流通餘額（全平台錢包總和）</div>
                      <div className="text-lg font-bold tabular-nums">
                        {fmtCoin(wallet?.paid_coin_total)} coin
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Free coin */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Gift className="h-4 w-4" />
                    Free Coin 流動
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs text-muted-foreground">本期發放</div>
                      <div className="font-medium tabular-nums flex items-center gap-2">
                        {fmtCoin(summary.free_coin_flow.granted)} coin
                        {deltas && <DeltaBadge d={deltas.free_granted} />}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">本期消費</div>
                      <div className="font-medium tabular-nums flex items-center gap-2">
                        {fmtCoin(summary.free_coin_flow.consumed)} coin
                        {deltas && <DeltaBadge d={deltas.free_consumed} />}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">消化率</div>
                      <div className="font-medium tabular-nums">
                        {fmtPct(summary.free_coin_flow.consumption_ratio, 1)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">目前流通餘額</div>
                      <div className="font-medium tabular-nums">
                        {fmtCoin(wallet?.free_coin_total)} coin
                      </div>
                    </div>
                    <div className="col-span-2 pt-2 border-t">
                      <div className="text-xs text-muted-foreground mb-1.5">發放來源明細</div>
                      <div className="space-y-1">
                        {Object.entries(summary.free_coin_flow.granted_by_type)
                          .sort(([, a], [, b]) => b.coins - a.coins)
                          .map(([type, b]) => (
                            <div key={type} className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">{grantLabel(type)}</span>
                              <span className="tabular-nums font-medium">
                                {fmtCoin(b.coins)} coin · {b.transaction_count} 次
                              </span>
                            </div>
                          ))}
                        {Object.keys(summary.free_coin_flow.granted_by_type).length === 0 && (
                          <span className="text-xs text-muted-foreground">本期無發放</span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* === Section 3 — 每日趨勢 === */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">每日趨勢</CardTitle>
            </CardHeader>
            <CardContent>
              {daily.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => v.substring(5)}
                    />
                    <YAxis
                      yAxisId="ntd"
                      tick={{ fontSize: 11 }}
                      label={{ value: "NTD", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
                    />
                    <YAxis
                      yAxisId="coin"
                      orientation="right"
                      tick={{ fontSize: 11 }}
                      label={{ value: "coin", angle: 90, position: "insideRight", style: { fontSize: 10 } }}
                    />
                    <Tooltip
                      formatter={(v: number, name: string) => {
                        if (name.includes("NTD") || name.includes("分潤") || name.includes("收入")) {
                          return [fmtNTD(v), name];
                        }
                        return [fmtCoin(v), name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line
                      yAxisId="ntd"
                      type="monotone"
                      dataKey="iap_gross_ntd"
                      name="IAP 收入"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId="ntd"
                      type="monotone"
                      dataKey="iap_net_ntd"
                      name="平台淨收入"
                      stroke="#16a34a"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId="ntd"
                      type="monotone"
                      dataKey="payout_ntd"
                      name="付給片商"
                      stroke="#b45309"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      yAxisId="coin"
                      type="monotone"
                      dataKey="paid_coin_consumed"
                      name="Paid coin 消費"
                      stroke="#9333ea"
                      strokeWidth={1.5}
                      strokeDasharray="4 2"
                      dot={false}
                    />
                    <Line
                      yAxisId="coin"
                      type="monotone"
                      dataKey="free_coin_consumed"
                      name="Free coin 消費"
                      stroke="#a3a3a3"
                      strokeWidth={1.5}
                      strokeDasharray="4 2"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <AdminEmptyState title="本期暫無數據" />
              )}
            </CardContent>
          </Card>

          {/* === Section 4 — 金幣包銷售排行 === */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">金幣包銷售排行</CardTitle>
            </CardHeader>
            <CardContent>
              {productSales.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={productSales}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="product_id" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: number) => fmtNTD(v)} />
                      <Bar dataKey="ntd" fill="#2563eb" name="營收" />
                    </BarChart>
                  </ResponsiveContainer>
                  <AdminDataTable
                    columns={productColumns}
                    data={productSales}
                    getRowId={(r: ProductSale) => r.product_id}
                    pageSize={10}
                    renderCell={(r: ProductSale, col) => {
                      switch (col.key) {
                        case "product_id":
                          return <span className="font-mono text-xs">{r.product_id}</span>;
                        case "transaction_count":
                          return <span className="tabular-nums">{r.transaction_count.toLocaleString()}</span>;
                        case "coins":
                          return <span className="tabular-nums">{fmtCoin(r.coins)}</span>;
                        case "ntd":
                          return <span className="tabular-nums font-medium">{fmtNTD(r.ntd)}</span>;
                        case "share":
                          return <span className="tabular-nums text-muted-foreground">{fmtPct(r.share, 1)}</span>;
                        default:
                          return null;
                      }
                    }}
                  />
                </div>
              ) : (
                <AdminEmptyState title="本期無金幣包銷售" />
              )}
            </CardContent>
          </Card>

          {/* === Section 5 — Top 20 劇集 === */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">Top 20 劇集（按分潤排序）</CardTitle>
            </CardHeader>
            <CardContent>
              <AdminDataTable
                columns={topDramaColumns}
                data={topDramas}
                emptyTitle="本期暫無劇集表現"
                pageSize={20}
                defaultSort={{ key: "payout_ntd", dir: "desc" }}
                getRowId={(r: TopDrama) => r.drama_id}
                onRowClick={(r: TopDrama) =>
                  navigate(`/financial/revenue/drama/${r.drama_id}?period=${period}`)
                }
                renderCell={(r: TopDrama, col) => {
                  switch (col.key) {
                    case "drama_title":
                      return (
                        <div>
                          <div className="font-medium">{r.drama_title}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{r.drama_id}</div>
                        </div>
                      );
                    case "company_name":
                      return <span className="text-sm">{r.company_name || "—"}</span>;
                    case "unlock_count":
                      return (
                        <span className="tabular-nums">
                          {r.unlock_count.toLocaleString()}
                          {r.full_drama_count > 0 && (
                            <span className="text-[10px] text-muted-foreground ml-1">
                              +{r.full_drama_count} 全劇
                            </span>
                          )}
                        </span>
                      );
                    case "paid_coin_used":
                      return <span className="tabular-nums">{fmtCoin(r.paid_coin_used)}</span>;
                    case "free_coin_used":
                      return <span className="tabular-nums text-muted-foreground">{fmtCoin(r.free_coin_used)}</span>;
                    case "unique_users":
                      return <span className="tabular-nums text-muted-foreground">{r.unique_users.toLocaleString()}</span>;
                    case "paid_coin_rate":
                      return (
                        <span className="tabular-nums text-xs text-muted-foreground">
                          {r.paid_coin_rate > 0 ? r.paid_coin_rate.toFixed(3) : "未設定"}
                        </span>
                      );
                    case "payout_ntd":
                      return (
                        <span className="tabular-nums font-medium text-amber-700 dark:text-amber-400">
                          {fmtNTD(r.payout_ntd)}
                        </span>
                      );
                    default:
                      return null;
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* === Section 6 — 片商分潤 === */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold">片商分潤合計</CardTitle>
            </CardHeader>
            <CardContent>
              <AdminDataTable
                columns={companyColumns}
                data={byCompany}
                emptyTitle="本期暫無營收數據"
                pageSize={20}
                defaultSort={{ key: "payout_ntd", dir: "desc" }}
                getRowId={(r: CompanyRow) => r.company_id}
                onRowClick={(r: CompanyRow) =>
                  navigate(`/financial/revenue/company/${r.company_id}?period=${period}`)
                }
                renderCell={(r: CompanyRow, col) => {
                  switch (col.key) {
                    case "company_name":
                      return <span className="font-medium">{r.company_name || r.company_id}</span>;
                    case "paid_coin_used":
                      return <span className="tabular-nums">{fmtCoin(r.paid_coin_used)}</span>;
                    case "free_coin_used":
                      return <span className="tabular-nums text-muted-foreground">{fmtCoin(r.free_coin_used)}</span>;
                    case "payout_ntd":
                      return (
                        <span className="tabular-nums font-medium text-amber-700 dark:text-amber-400">
                          {fmtNTD(r.payout_ntd)}
                        </span>
                      );
                    case "transaction_count":
                      return <span className="tabular-nums text-muted-foreground">{r.transaction_count.toLocaleString()}</span>;
                    case "unique_users":
                      return <span className="tabular-nums text-muted-foreground">{r.unique_users.toLocaleString()}</span>;
                    default:
                      return null;
                  }
                }}
              />
            </CardContent>
          </Card>
        </div>
      )}
    </AdminPage>
  );
}

// Preserved import for internal use  (PeriodCoreMetrics used via summary prop type inference)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _PreserveImport = PeriodCoreMetrics;
